import type { LanguageCode } from "../api/contracts/language-settings";
import { getI18nLanguage, type I18nLanguage } from "./language";
import { languageCodeFromTag, servedI18nLanguage } from "./language-codes";

/**
 * The language to open in, from the browser's own preference order.
 *
 * Walks `navigator.languages` rather than only `navigator.language`: a guest
 * whose first preference is a language this booking engine does not serve
 * should still get their second one before falling back to English. Which of
 * these the site actually offers is decided afterwards, once its config has
 * loaded — this only says what the browser asked for.
 */
export const getBrowserLanguageCode = (): LanguageCode => {
  const preferences = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (const preference of preferences) {
    // `servedI18nLanguage` answers undefined rather than English for a
    // language we do not serve, which is the distinction this walk needs.
    const served = servedI18nLanguage(preference);
    if (served) return languageCodeFromTag(served);
  }

  return "en-GB";
};

export const getBrowserI18nLanguage = (): I18nLanguage =>
  getI18nLanguage(getBrowserLanguageCode());

/**
 * The best of the guest's browser preferences that this site actually offers.
 *
 * Used when a stored choice is no longer served: their second preference is a
 * better answer than English, which is what jumping straight to a default
 * would give a German-then-Swedish guest on a Swedish-and-English site.
 */
export const pickOfferedLanguage = (offered: LanguageCode[]): LanguageCode => {
  const preferences = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (const preference of preferences) {
    const served = servedI18nLanguage(preference);
    if (!served) continue;
    const code = languageCodeFromTag(served);
    if (offered.includes(code)) return code;
  }

  return offered.includes("en-GB") ? "en-GB" : offered[0];
};
